/**
 * Formatters - Localization & Data Formatting Utilities
 * 
 * ARCHITECTURAL REFACTOR: Centralized formatting functions
 * Prevents showing raw enums like "CREDIT_CARD" or "PENDING" to users
 */

/**
 * Format payment method enum to Turkish display text
 * @param method Payment method enum (CREDIT_CARD, CASH, etc.)
 * @returns Localized payment method string
 */
export function formatPaymentMethod(method: string): string {
  const methodMap: Record<string, string> = {
    CREDIT_CARD: 'Kredi Kartı',
    CASH: 'Kapıda Ödeme',
    DEBIT_CARD: 'Banka Kartı',
    WALLET: 'Cüzdan',
  };

  return methodMap[method] || method;
}

/**
 * Format order status enum to Turkish display text
 * @param status Order status enum (CREATED, PREPARING, etc.)
 * @returns Localized order status string
 */
export function formatOrderStatus(status: string): string {
  const statusMap: Record<string, string> = {
    CREATED: 'Sipariş Alındı',
    SCHEDULED: 'Planlandı',
    PENDING: 'Sipariş Alındı', // PENDING also shows as "Sipariş Alındı"
    PREPARING: 'Hazırlanıyor',
    READY: 'Hazır',
    ON_WAY: 'Yolda',
    DELIVERED: 'Teslim Edildi',
    CANCELLED: 'İptal Edildi',
  };

  return statusMap[status] || status;
}

/**
 * Format price in TRY
 * @param amount Price in TRY (e.g., 85.00)
 * @returns Formatted price string (e.g., "85.00 TL")
 */
export function formatPrice(amount: number | string | null | undefined): string {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  if (isNaN(numericAmount) || numericAmount == null) {
    return '0.00 TL';
  }
  return numericAmount.toFixed(2) + ' TL';
}

/**
 * Format currency amount (already in TRY, not cents)
 * @param amount Amount in TRY
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number | string | null | undefined): string {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  if (isNaN(numericAmount) || numericAmount == null) {
    return '0.00 TL';
  }
  return numericAmount.toFixed(2) + ' TL';
}

/**
 * Canonical map of customer cancellation reason codes → Turkish display labels.
 * Single source of truth shared between the cancel modal and any read-only display.
 */
export const CANCELLATION_REASON_LABELS: Record<string, string> = {
  wrong_address: 'Yanlış adres seçildi',
  wrong_order: 'Yanlış sipariş verdim',
  change_plan: 'Planım değişti',
  duplicate: 'Yanlışlıkla tekrar sipariş verdim',
  other: 'Diğer',
};

/**
 * Resolves a stored cancellation reason code to its Turkish display label.
 * Free-text reasons (e.g. submitted via the "Diğer" path) are returned as-is.
 */
export function formatCancellationReason(reason: string | null | undefined): string {
  if (!reason) return '';
  return CANCELLATION_REASON_LABELS[reason] ?? reason;
}

/**
 * Format payment status enum to Turkish display text
 * @param status Payment status enum (PENDING, PAID, etc.)
 * @returns Localized payment status string
 */
export function formatPaymentStatus(status: string): string {
  const statusMap: Record<string, string> = {
    PENDING: 'Bekliyor',
    PAID: 'Ödendi',
    FAILED: 'Başarısız',
    REFUNDED: 'İade Edildi',
  };

  return statusMap[status] || status;
}
